import { FC } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
// Components
import {
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
} from '@mui/material';
import { ExpandMore, Edit, Delete, Download, Clear, Check } from '@mui/icons-material';
import { resolveObjectField } from '../../helpers/util';

interface IMobileTable {
  original: any;
  handleEdit?: (val: any) => void;
  handleDelete?: (val: any) => void;
  handleDownload?: (val: any) => void;
  handleApproval?: (val: any) => void;
  handleAccordionLabel?: (val: any) => void;
  fields: {
    name: string;
    accessor: string;
    Cell?: (value: { cell: { row: { original: Record<string, unknown> } } }) => JSX.Element;
  }[];
  mobileAccordionLabel?: (original: []) => string;
}

export const MobileTable: FC<IMobileTable> = ({
  handleDownload,
  handleApproval,
  handleEdit,
  handleDelete,
  fields,
  original,
  mobileAccordionLabel,
}) => {
  const classes = useStyles();
  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography color="primary" className={classes.boldName}>
            {mobileAccordionLabel ? mobileAccordionLabel?.(original) : original[fields[0].accessor]}
          </Typography>
          <div className={classes.buttonsWrapper}>
            {handleDownload && (
              <IconButton
                className={classes.editButton}
                color="primary"
                onClick={e => {
                  e.stopPropagation();
                  handleDownload(original);
                }}
                size="large"
              >
                <Download />
              </IconButton>
            )}
            {handleEdit && (
              <IconButton
                className={classes.editButton}
                color="primary"
                onClick={e => {
                  e.stopPropagation();
                  handleEdit(original);
                }}
                size="large"
              >
                <Edit />
              </IconButton>
            )}
            {handleDelete && (
              <IconButton
                className={classes.deleteButton}
                color="default"
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(original);
                }}
                size="large"
              >
                <Delete />
              </IconButton>
            )}
            {handleApproval && (
              <IconButton
                className={
                  original.paymentStatus === 'Approved'
                    ? classes.rejectButton
                    : classes.approvalButton
                }
                color="default"
                onClick={e => {
                  e.stopPropagation();
                  handleApproval(original);
                }}
                size="large"
              >
                {original.paymentStatus === 'Approved' ? <Clear /> : <Check />}
              </IconButton>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        {fields.map((field: typeof fields[0], index) => {
          return (
            <div key={`${index}`} className={classes.panelSummaryWrapper}>
              <FormLabel component="span" className={clsx(classes.boldName, classes.subLabelEmail)}>
                {field?.name?.length > 1 ? `${field.name}:` : field.name}
              </FormLabel>
              <Typography component="span">
                {/* TODO clean this up */}
                {typeof field.accessor === 'string' && typeof field.Cell !== 'function'
                  ? !resolveObjectField(field.accessor, original)
                    ? '--'
                    : resolveObjectField(field.accessor, original)
                  : typeof field.Cell === 'function'
                  ? ''
                  : '--'}
                {typeof field.Cell === 'function'
                  ? field.Cell({ cell: { row: { original } } })
                  : null}
              </Typography>
            </div>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid #e0e0e0`,
    overflowX: 'hidden',
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3,
    },
  },
  topPanelSummaryWrapper: {
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 },
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 },
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: '#f5f5f5',
  },
  editButton: {
    padding: `2px 5px`,
  },
  deleteButton: {
    padding: `2px 5px`,
    color: '#A94442',
  },
  approvalButton: {
    padding: `2px`,
    marginRight: '6px',
    backgroundColor: '#3C763D',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#3C763D',
      color: '#ffffff',
    },
    '& svg': {
      width: '.75em',
      height: '.75em',
    },
  },
  rejectButton: {
    padding: `2px`,
    marginRight: '6px',
    backgroundColor: '#A94442',
    color: '#ffffff',
    '&:hover': { backgroundColor: '#A94442', color: '#ffffff' },
    '& svg': {
      width: '.75em',
      height: '.75em',
    },
  },
  boldName: {
    color: '#3C763D',
    fontWeight: 600,
  },
  subLabelEmail: {
    marginRight: 10,
  },
  buttonsWrapper: {
    display: 'flex',
  },
}));
